import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useLayoutEffect,
} from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../assets/close-2.svg';
import { Flex, FlexColumn } from '../../Flex';
import IconButton from '../../IconButton';
import { Announcement } from '../types';

const ROTATION_INTERVAL_MS = 8000;
const SLIDE_DURATION_MS = 400;
const STORAGE_KEY_PREFIX = 'announcement-dismissed';

const hashString = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash * 31 + str.charCodeAt(i)) | 0) >>> 0;
  }
  return hash;
};

const getAnnouncementKey = (a: Announcement) =>
  a.id
    ? `${STORAGE_KEY_PREFIX}-${a.id}`
    : `${STORAGE_KEY_PREFIX}-${hashString(`${a.prefix}:${a.message}`)}`;

const isDismissed = (a: Announcement) => {
  try {
    return localStorage.getItem(getAnnouncementKey(a)) === 'true';
  } catch {
    return false;
  }
};

const Wrapper = styled(Flex)`
  align-items: flex-start;
  align-self: center;
  width: calc(100% - 14px);
  border-radius: 6px 6px 0 0;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
      90deg,
      rgba(249, 196, 255, 0.33) 0%,
      rgba(161, 196, 253, 0.33) 100%
    ),
    ${({ theme }) => theme.palette.common.white};
  box-shadow: 0px -6px 15px -5px rgba(0, 0, 0, 0.03);
  transition: height ${SLIDE_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const Track = styled(FlexColumn)`
  align-items: center;
  flex: 1;
  min-width: 0;
  transition: transform ${SLIDE_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const Item = styled(Typography).attrs({ component: 'div' as const })`
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.12px;
  color: ${({ theme }) => theme.palette.common.gray2};
  text-align: center;
  flex-shrink: 0;
  width: 100%;
  padding: 2px 24px;
  box-sizing: border-box;
  margin: 0;
`;

const Prefix = styled.span`
  font-weight: 600;
`;

const CtaLink = styled.button.attrs({ type: 'button' })`
  border: 0;
  padding: 0;
  background: transparent;
  font: inherit;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.common.blue7};
  cursor: pointer;
  margin-left: 6px;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

const DismissButton = styled(IconButton)`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  padding: 2px;
`;

const CloseIconStyled = styled(CloseIcon)`
  width: 10px;
  height: 10px;
  path {
    fill: ${({ theme }) => theme.palette.common.gray4};
  }
`;

type Props = {
  announcements: Announcement[];
};

const AnnouncementsBar = ({ announcements }: Props) => {
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(() => {
    const keys = new Set<string>();
    announcements.forEach((a) => {
      if (isDismissed(a)) keys.add(getAnnouncementKey(a));
    });
    return keys;
  });

  const visibleAnnouncements = useMemo(
    () =>
      announcements.filter((a) => !dismissedKeys.has(getAnnouncementKey(a))),
    [announcements, dismissedKeys],
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [sliding, setSliding] = useState(false);
  const [currentHeight, setCurrentHeight] = useState(0);
  const [nextHeight, setNextHeight] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const currentItemRef = useRef<HTMLDivElement>(null);
  const nextItemRef = useRef<HTMLDivElement>(null);

  const safeIndex = currentIndex % (visibleAnnouncements.length || 1);
  const nextIndex = (safeIndex + 1) % (visibleAnnouncements.length || 1);

  useLayoutEffect(() => {
    if (currentItemRef.current) {
      setCurrentHeight(currentItemRef.current.offsetHeight);
    }
    if (nextItemRef.current) {
      setNextHeight(nextItemRef.current.offsetHeight);
    }
  }, [safeIndex, visibleAnnouncements]);

  const triggerSlide = useCallback(() => {
    setSliding(true);

    timeoutRef.current = setTimeout(() => {
      setSliding(false);
      setCurrentIndex((prev) => (prev + 1) % visibleAnnouncements.length);
    }, SLIDE_DURATION_MS);
  }, [visibleAnnouncements.length]);

  useEffect(() => {
    if (visibleAnnouncements.length <= 1) return undefined;

    const intervalId = setInterval(triggerSlide, ROTATION_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [visibleAnnouncements.length, triggerSlide]);

  const handleDismiss = useCallback(() => {
    const current = visibleAnnouncements[safeIndex];
    if (!current) return;
    const key = getAnnouncementKey(current);
    try {
      localStorage.setItem(key, 'true');
    } catch {
      // storage full or unavailable
    }
    setDismissedKeys((prev) => new Set(prev).add(key));
    setCurrentIndex((prev) =>
      prev >= visibleAnnouncements.length - 1 ? 0 : prev,
    );
  }, [visibleAnnouncements, safeIndex]);

  if (!visibleAnnouncements.length) return null;

  const current = visibleAnnouncements[safeIndex];
  const next = visibleAnnouncements[nextIndex];
  const wrapperHeight = sliding ? nextHeight : currentHeight;

  const renderContent = (a: Announcement) => {
    const { cta } = a;
    return (
      <>
        <Prefix>{a.prefix}: </Prefix>
        {a.message}
        {cta && (
          <CtaLink
            onClick={(e) => {
              e.stopPropagation();
              cta.onClick();
            }}
          >
            {cta.label}
          </CtaLink>
        )}
      </>
    );
  };

  return (
    <Wrapper
      style={
        visibleAnnouncements.length > 1
          ? { height: wrapperHeight || undefined }
          : undefined
      }
    >
      {visibleAnnouncements.length > 1 ? (
        <Track
          key={safeIndex}
          style={{
            transform: sliding
              ? `translateY(-${currentHeight}px)`
              : 'translateY(0)',
          }}
        >
          <Item ref={currentItemRef}>{renderContent(current)}</Item>
          <Item ref={nextItemRef}>{renderContent(next)}</Item>
        </Track>
      ) : (
        <Item>{renderContent(current)}</Item>
      )}
      <DismissButton
        onClick={handleDismiss}
        size="small"
        aria-label="Dismiss announcement"
      >
        <CloseIconStyled />
      </DismissButton>
    </Wrapper>
  );
};

export default AnnouncementsBar;
